import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Anchor,
  Button,
  Checkbox,
  Group,
  Input,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { EditOrganizationModal, Icon, type IconName, LoadMorePagination } from '@/components';
import { CUSTOM_ICONS, ORGANIZATION_TAB, SORT_BY, SORT_ORDER } from '@/types';
import {
  CHURCH_PROGRAM,
  CHURCH_TYPE,
  type ChurchCreateRequest,
  type ChurchListing,
  ORGANIZATION_MODAL_MODE,
  PROGRAM_LABELS,
} from '@/types/churches';
import { churchesService } from '@/services/churches.service';
import { useChurches, useCreateChurchMutation } from '@/hooks/churches';
import { useDebounceValue } from 'usehooks-ts';
import styles from './Organizations.module.sass';

const LAYOUT = {
  TABLE_ICON_SIZE: 35,
  SEARCH_ICON_SIZE: 20,
  TABLE_FILTER_ICON_SIZE: 30,
  SKELETON_HEIGHT: 20,
  SKELETON_ROW_COUNT: 10,
  SKELETON_ICON_COUNT: 5,
  ORGANIZATIONS_PER_PAGE_LIMIT: 10,
};

const getOrganizationTypeIcon = (type: CHURCH_TYPE) => {
  const iconMap: Record<CHURCH_TYPE, CUSTOM_ICONS> = {
    [CHURCH_TYPE.DIOCESE]: CUSTOM_ICONS.DIOCESE_BUILDING_SOLID,
    [CHURCH_TYPE.PARISH]: CUSTOM_ICONS.CHURCH_SOLID,
    [CHURCH_TYPE.CLUSTER]: CUSTOM_ICONS.CHURCH_SOLID,
  };

  return iconMap[type];
};

const getLocationText = (organization: ChurchListing) =>
  [organization.address1 || organization.address2, organization.city, organization.state].filter(Boolean).join(', ') +
  (organization.zipCode ? ` ${organization.zipCode}` : '');

const getProgramIcon = (program: CHURCH_PROGRAM) => {
  const iconMap: Partial<Record<CHURCH_PROGRAM, IconName>> = {
    mpa: CUSTOM_ICONS.MY_PARISH,
    camino: CUSTOM_ICONS.CAMINO,
    mio: CUSTOM_ICONS.MASS_INTENTIONS,
    ministry_scheduler: CUSTOM_ICONS.MINISTRIES,
    adoration: CUSTOM_ICONS.ADORATION,
    facility_sched: CUSTOM_ICONS.FACILITIES,
    forms: CUSTOM_ICONS.FORMS,
    messages: CUSTOM_ICONS.MESSAGES,
  };

  return iconMap[program];
};

const FILTERS = [
  CHURCH_PROGRAM.MPA,
  CHURCH_PROGRAM.CAMINO,
  CHURCH_PROGRAM.MIO,
  CHURCH_PROGRAM.MINISTRY_SCHEDULER,
  CHURCH_PROGRAM.ADORATION,
  CHURCH_PROGRAM.FACILITY_SCHED,
  CHURCH_PROGRAM.FORMS,
  CHURCH_PROGRAM.MESSAGES,
];

export const Organizations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedPrograms, setSelectedPrograms] = useState<CHURCH_PROGRAM[]>(
    searchParams.getAll('programs') as CHURCH_PROGRAM[]
  );
  const [extraItems, setExtraItems] = useState<ChurchListing[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<SORT_BY>(SORT_BY.NAME);
  const [sortOrder, setSortOrder] = useState<SORT_ORDER>(SORT_ORDER.ASC);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 500);
  const searchTermLength = useMemo(() => searchTerm.trim().length, [debouncedSearchTerm]);
  const MIN_SEARCH_LENGTH = 3;

  const queryParams = useMemo(
    () => ({
      term: searchTermLength >= MIN_SEARCH_LENGTH ? debouncedSearchTerm.trim() : undefined,
      programs: selectedPrograms.length > 0 ? selectedPrograms : undefined,
      limit: LAYOUT.ORGANIZATIONS_PER_PAGE_LIMIT,
      sortBy,
      sortOrder,
    }),
    [debouncedSearchTerm, selectedPrograms, sortBy, sortOrder, searchTermLength]
  );

  const shouldFetch = !(searchTermLength > 0 && searchTermLength < MIN_SEARCH_LENGTH);
  const { data, isLoading } = useChurches(queryParams, shouldFetch);
  const createChurchMutation = useCreateChurchMutation();

  const firstPageItems = data?.items ?? [];
  const totalOrganizations = data?.total ?? 0;
  const organizations = useMemo(() => [...firstPageItems, ...extraItems], [firstPageItems, extraItems]);
  const hasMore = organizations.length < totalOrganizations;

  useEffect(() => {
    setExtraItems([]);
    if (firstPageItems.length > 0) {
      setCursor(firstPageItems[firstPageItems.length - 1].id);
    } else {
      setCursor(undefined);
    }
  }, [data]);

  type ClickableCellProps = {
    to: string;
    ariaLabel: string;
    className?: string;
    children: ReactNode;
  };

  const ClickableCell = ({ to, ariaLabel, className, children }: ClickableCellProps) => (
    <Table.Td className={className}>
      <Anchor
        component={Link}
        to={to}
        state={{ fromOrganizations: true }}
        className={styles.cellLink}
        onClick={(e) => e.stopPropagation()}
        aria-label={ariaLabel}
      />
      {children}
    </Table.Td>
  );

  const handleProgramFilterChange = (value: string[]) => {
    const programs = value as CHURCH_PROGRAM[];
    setSelectedPrograms(programs);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('programs');
    programs.forEach((program) => {
      newSearchParams.append('programs', program);
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleSort = (column: SORT_BY) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
    } else {
      setSortBy(column);
      setSortOrder(SORT_ORDER.ASC);
    }
    setExtraItems([]);
    setCursor(undefined);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newSearchParams = new URLSearchParams(searchParams);
    if (value.trim()) {
      newSearchParams.set('search', value);
    } else {
      newSearchParams.delete('search');
    }
    setSearchTerm(value);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await churchesService.getChurches({
        ...queryParams,
        cursor,
      });
      setExtraItems((prev) => [...prev, ...response.items]);
      if (response.items.length > 0) {
        setCursor(response.items[response.items.length - 1].id);
      }
    } catch (error) {
      console.error('Failed to load more organizations:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    const urlSearchTerm = searchParams.get('search') || '';
    const urlPrograms = searchParams.getAll('programs') as CHURCH_PROGRAM[];

    if (urlSearchTerm !== searchTerm) {
      setSearchTerm(urlSearchTerm);
    }

    if (JSON.stringify(urlPrograms.sort()) !== JSON.stringify(selectedPrograms.sort())) {
      setSelectedPrograms(urlPrograms);
    }
  }, [searchParams]);

  const handleRowClick = (organizationId: string) => {
    navigate(`/organizations/${organizationId}`, { state: { fromOrganizations: true } });
  };

  const handleCreateClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateOrganization = async (data: ChurchCreateRequest) => {
    await createChurchMutation.mutateAsync(data);
    setIsCreateModalOpen(false);
  };

  const skeletonRows = Array.from({ length: LAYOUT.SKELETON_ROW_COUNT }).map((_, index) => (
    <Table.Tr key={`skeleton-${index}`} className={styles.tableRow}>
      <Table.Td className={styles.iconColumn}>
        <Skeleton height={LAYOUT.TABLE_ICON_SIZE} width={LAYOUT.TABLE_ICON_SIZE} radius="sm" />
      </Table.Td>
      <Table.Td className={styles.nameColumn}>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="80%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.clusterColumn}>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="60%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.locationColumn}>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="70%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.dioceseColumn}>
        <Skeleton height={LAYOUT.SKELETON_HEIGHT} width="50%" radius="sm" />
      </Table.Td>
      <Table.Td className={styles.programsColumn}>
        <Group gap="md">
          {Array.from({ length: LAYOUT.SKELETON_ICON_COUNT }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              height={LAYOUT.TABLE_FILTER_ICON_SIZE}
              width={LAYOUT.TABLE_FILTER_ICON_SIZE}
              radius="sm"
            />
          ))}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  const rows =
    organizations?.length > 0 ? (
      organizations.map((organization, _index) => {
        const cellProps = {
          to: `/organizations/${organization.id}/${ORGANIZATION_TAB.ORG_ADMINS}`,
          ariaLabel: `Open ${organization.name}`,
        };

        return (
          <Table.Tr
            key={organization.id + _index}
            className={styles.tableRow}
            onClick={() => handleRowClick(organization.id)}
          >
            <ClickableCell {...cellProps} className={styles.iconColumn}>
              <Icon
                name={getOrganizationTypeIcon(organization.type)}
                className={styles.churchTypeIcon}
                size={LAYOUT.TABLE_ICON_SIZE}
              />
            </ClickableCell>
            <ClickableCell {...cellProps} className={styles.nameColumn}>
              {organization.name}
            </ClickableCell>
            <ClickableCell {...cellProps} className={styles.clusterColumn}>
              {organization.clusterName || ''}
            </ClickableCell>
            <ClickableCell {...cellProps} className={styles.locationColumn}>
              {getLocationText(organization)}
            </ClickableCell>
            <ClickableCell {...cellProps} className={styles.dioceseColumn}>
              {organization.diocese?.name || ''}
            </ClickableCell>
            <ClickableCell {...cellProps} className={styles.programsColumn}>
              <Group gap="md">
                {Object.keys(organization.programs)
                  .filter((program) => organization.programs[program as keyof typeof organization.programs])
                  .map((program) => (
                    <Tooltip key={program} label={PROGRAM_LABELS[program as CHURCH_PROGRAM]}>
                      <Icon
                        name={getProgramIcon(program as CHURCH_PROGRAM) as IconName}
                        size={LAYOUT.TABLE_FILTER_ICON_SIZE}
                      />
                    </Tooltip>
                  ))}
              </Group>
            </ClickableCell>
          </Table.Tr>
        );
      })
    ) : (
      <Table.Tr>
        <Table.Td colSpan={6} className={styles.noOrganizationsFound}>
          <Text>No organizations found</Text>
        </Table.Td>
      </Table.Tr>
    );

  return (
    <Stack mx="xxl" py="md" gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="md" align="center" wrap="nowrap" className={styles.searchGroup}>
          <Button leftSection={<Icon name="IconPlus" />} onClick={handleCreateClick} className={styles.toolbarControl}>
            Create Organization
          </Button>
          <TextInput
            placeholder="Search"
            leftSection={<Icon name="IconSearch" className={styles.searchIcon} size={LAYOUT.SEARCH_ICON_SIZE} />}
            rightSection={
              searchTerm !== '' ? (
                <Input.ClearButton
                  onClick={() => {
                    setSearchTerm('');
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('search');
                    setSearchParams(newSearchParams, { replace: true });
                  }}
                />
              ) : undefined
            }
            value={searchTerm}
            onChange={handleSearchChange}
            classNames={{ root: styles.searchInputRoot, input: styles.searchInput }}
            error={
              searchTermLength > 0 && searchTermLength < MIN_SEARCH_LENGTH
                ? `Enter at least ${MIN_SEARCH_LENGTH} characters`
                : undefined
            }
          />
        </Group>

        <ScrollArea type="hover" scrollbarSize={4} offsetScrollbars="x" className={styles.programFiltersScrollArea}>
          <Checkbox.Group
            value={selectedPrograms}
            onChange={handleProgramFilterChange}
            className={styles.programFiltersCheckboxGroup}
          >
            <Group gap="md" wrap="nowrap" align="center" className={styles.programFiltersGroup}>
              {FILTERS.map((program) => (
                <Checkbox.Card
                  key={program}
                  value={program}
                  w="fit-content"
                  bg="white"
                  className={styles.programFilterCard}
                >
                  <Group gap="xs" px="xs" wrap="nowrap" className={styles.programFilterCardContent}>
                    <Checkbox.Indicator />
                    <Group gap="xs" wrap="nowrap" className={styles.programFilterCardLabel}>
                      <Text>{PROGRAM_LABELS[program]}</Text>
                      <Icon name={getProgramIcon(program) as IconName} size={LAYOUT.TABLE_FILTER_ICON_SIZE} />
                    </Group>
                  </Group>
                </Checkbox.Card>
              ))}
            </Group>
          </Checkbox.Group>
        </ScrollArea>
      </Group>

      <Paper className={styles.tableContainer}>
        <Table withTableBorder highlightOnHover>
          <Table.Thead className={styles.tableHeader}>
            <Table.Tr>
              <Table.Th className={styles.iconColumn}></Table.Th>
              <Table.Th
                className={styles.nameColumn}
                aria-sort={
                  sortBy === SORT_BY.NAME ? (sortOrder === SORT_ORDER.ASC ? 'ascending' : 'descending') : 'none'
                }
              >
                <Group gap="xs">
                  <Text>Name</Text>
                  <UnstyledButton
                    onClick={() => handleSort(SORT_BY.NAME)}
                    className={styles.sortButton}
                    disabled={isLoading || isLoadingMore}
                    aria-label="Sort by Name"
                  >
                    <Icon
                      name={
                        sortBy === SORT_BY.NAME
                          ? sortOrder === SORT_ORDER.ASC
                            ? 'IconArrowUp'
                            : 'IconArrowDown'
                          : 'IconSelector'
                      }
                      size={16}
                      className={styles.sortIcon}
                    />
                  </UnstyledButton>
                </Group>
              </Table.Th>
              <Table.Th className={styles.clusterColumn}>Cluster Name</Table.Th>
              <Table.Th className={styles.locationColumn}>Location</Table.Th>
              <Table.Th
                className={styles.dioceseColumn}
                aria-sort={
                  sortBy === SORT_BY.DIOCESE ? (sortOrder === SORT_ORDER.ASC ? 'ascending' : 'descending') : 'none'
                }
              >
                <Group gap="xs">
                  <Text>Diocese</Text>
                  <UnstyledButton
                    onClick={() => handleSort(SORT_BY.DIOCESE)}
                    className={styles.sortButton}
                    disabled={isLoading || isLoadingMore}
                    aria-label="Sort by Diocese"
                  >
                    <Icon
                      name={
                        sortBy === SORT_BY.DIOCESE
                          ? sortOrder === SORT_ORDER.ASC
                            ? 'IconArrowUp'
                            : 'IconArrowDown'
                          : 'IconSelector'
                      }
                      size={16}
                      className={styles.sortIcon}
                    />
                  </UnstyledButton>
                </Group>
              </Table.Th>
              <Table.Th className={styles.programsColumn}>Programs</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{isLoading ? skeletonRows : rows}</Table.Tbody>
        </Table>
      </Paper>

      <LoadMorePagination
        loaded={organizations.length}
        total={totalOrganizations}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      <EditOrganizationModal
        opened={isCreateModalOpen}
        onClose={handleCreateModalClose}
        mode={ORGANIZATION_MODAL_MODE.CREATE}
        onCreate={handleCreateOrganization}
      />
    </Stack>
  );
};
